import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, BellRing, ChevronDown, ChevronUp } from "lucide-react";

const STAGE = {
  overdue: { label: (d) => `Overdue ${d}d`, v: "destructive" },
  due_today: { label: () => "Due today", v: "default" },
  due_soon: { label: (d) => `Due in ${d}d`, v: "secondary" },
};

export function RemindersPanel({ onSent }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const load = () => api.get("/admin/reminders").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const remind = async (it) => {
    try {
      const { data: r } = await api.post(`/admin/orders/${it.order_id}/remind`, { origin: window.location.origin });
      window.open(r.whatsapp_url, "_blank", "noopener");
      toast.success(`WhatsApp opened for ${it.customer}${r.phone ? "" : " (no phone on file - pick the contact)"} · reminder #${r.reminders_sent} logged`);
      load();
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!data || data.items.length === 0) return null;
  const c = data.counts;
  return (
    <Card className="mb-4 overflow-hidden border-amber-400/40" data-testid="reminders-panel">
      <button className="w-full px-4 py-3 flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="reminders-toggle">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-amber-600" />
          <span className="font-display font-semibold text-sm">Payment reminders</span>
          <span className="text-xs text-muted-foreground">· {c.overdue} overdue · {c.due_today} due today · {c.due_soon} due within {data.days_before} days</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {data.items.map((it) => (
                <tr key={it.order_id} className="border-b border-border/40 last:border-0" data-testid={`reminder-row-${it.order_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{it.invoice_no}</td>
                  <td className="px-4 py-2">{it.customer}<div className="text-[11px] text-muted-foreground">{it.customer_phone || "no phone on file"} · agent {it.agent}</div></td>
                  <td className="px-4 py-2 text-right font-medium">{money(it.amount)}</td>
                  <td className="px-4 py-2"><Badge variant={STAGE[it.stage].v}>{STAGE[it.stage].label(it.days)}</Badge><div className="text-[11px] text-muted-foreground mt-0.5">due {it.due_date}</div></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{it.reminders_sent ? `${it.reminders_sent} sent · last ${it.last_reminded_at?.slice(0, 10)}` : "not reminded yet"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" className="border-[#25D366]/60 text-[#128C7E] hover:bg-[#25D366]/10" onClick={() => remind(it)} data-testid={`remind-${it.order_id}`}>
                      <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp reminder
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-muted-foreground">One tap opens WhatsApp with the message and a secure invoice link pre-filled; you press send. Each reminder is logged on the invoice.</p>
        </div>
      )}
    </Card>
  );
}
